import { ReactElement, useEffect, useState } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { StyledPreviewImageBoxInternal } from "./styled-preview-image-box.partial";

type EnlargedImagePreviewBoxProps = {
  imageUrl?: string | null;
  bookingTypeContents: ReactElement;
  buttonChild: ReactElement;
  loading?: boolean;
  onViewClick: () => void;
};

/**
 * Enlarged Image Preview Box component
 * Displays booking type contents and action buttons regardless of the image loading state (Skeleton)
 */
export function EnlargedImagePreviewBox({
  imageUrl,
  bookingTypeContents,
  buttonChild,
  loading = false,
  onViewClick
}: Readonly<EnlargedImagePreviewBoxProps>) {
  const theme = useTheme();

  // Apply background styles only when an image URL is present
  const backgroundImage = imageUrl
    ? `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}00 120%), url(${imageUrl})`
    : "none";

  const [imageLoaded, setImageLoaded] = useState(false);

  // Preload image to determine loading state
  useEffect(() => {
    if (!imageUrl) return setImageLoaded(true);

    setImageLoaded(false);

    const img = new Image();
    img.src = imageUrl;

    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true);
  }, [imageUrl, loading]);

  // Check if the background content is ready to be displayed
  const isImageReady = !loading && (!imageUrl || imageLoaded);

  return (
    <Box
      data-testid="enlarged-image-preview-box"
      sx={{
        position: "relative",
        width: "100%",
        height: 180,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "grey.100"
      }}
    >
      {/* 1. Background Layer: Shows Skeleton while loading, then displays the styled image box */}
      {isImageReady ? (
        <StyledPreviewImageBoxInternal
          data-testid="enlarged-image-preview-styled-parent-box"
          backgroundImage={backgroundImage}
          onClick={onViewClick}
        />
      ) : (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation={false}
          sx={{ position: "absolute", top: 0, left: 0 }}
        />
      )}

      {/* 2. Overlay Layer: Always displayed regardless of the loading state */}

      {/* Booking type contents (e.g., icons) - Fixed at Top-Left */}
      <Box
        data-testid="enlarged-image-preview-booking-type-contents"
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2 // Ensure it stays above the Skeleton
        }}
      >
        {bookingTypeContents}
      </Box>

      {/* Action button (e.g., View Place) - Fixed at Bottom-Right */}
      <Box
        data-testid="enlarged-image-preview-button-child"
        sx={{
          position: "absolute",
          bottom: 10,
          right: 10,
          zIndex: 2,
          gap: 1
        }}
      >
        {buttonChild}
      </Box>
    </Box>
  );
}
